/* global google */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { VectorTile } from '@mapbox/vector-tile';
import Pbf from 'pbf';
import { PMTiles } from 'pmtiles';
import { useGoogleMap } from '@react-google-maps/api';
import { openLayerTray } from '@actions/map-actions';

// MVT tile extent (must match backend EXTENT constant)
const EXTENT = 4096;
const TILE_SIZE = 256;
// Pixel radius used for click-to-feature hit detection
const CLICK_THRESHOLD_PX = 8;
// Max number of rendered tiles to keep in memory cache
const TILE_CACHE_MAX = 256;

// ---------------------------------------------------------------------------
// Coordinate helpers
// ---------------------------------------------------------------------------

const tileXToLng = (px, tileX, zoom) => {
  const worldX = (tileX + px / EXTENT) / Math.pow(2, zoom);
  return worldX * 360 - 180;
};

const tileYToLat = (py, tileY, zoom) => {
  const tileCount = Math.pow(2, zoom);
  const worldFrac = (tileY + py / EXTENT) / tileCount;
  const rad = Math.PI - 2 * Math.PI * worldFrac;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(rad) - Math.exp(-rad)));
};

const geomToWgs84 = (rawGeometry, tileX, tileY, zoom) =>
  rawGeometry.map(ring =>
    ring.map(pt => ({
      lat: tileYToLat(pt.y, tileY, zoom),
      lng: tileXToLng(pt.x, tileX, zoom)
    }))
  );

// ---------------------------------------------------------------------------
// Distance utilities (WGS84, approximated as flat Euclidean)
// ---------------------------------------------------------------------------

const distanceSq = (ax, ay, bx, by) => (ax - bx) ** 2 + (ay - by) ** 2;

const pointToSegmentDistSq = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return distanceSq(px, py, ax, ay);
  }
  const ratio = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  return distanceSq(px, py, ax + ratio * dx, ay + ratio * dy);
};

const distSqToFeature = (rings, type, lat, lng) => {
  let min = Infinity;
  for (const ring of rings) {
    if (ring.length === 0) {
      continue; // eslint-disable-line no-continue
    }
    if (type === 1) {
      for (const pt of ring) {
        min = Math.min(min, distanceSq(lng, lat, pt.lng, pt.lat));
      }
    } else {
      const ringMin = ring.slice(0, -1).reduce((acc, pt, idx) => {
        const next = ring[idx + 1];
        const segDist = pointToSegmentDistSq(lng, lat, pt.lng, pt.lat, next.lng, next.lat);
        return Math.min(acc, segDist);
      }, Infinity);
      min = Math.min(min, ringMin);
    }
  }
  return min;
};

// ---------------------------------------------------------------------------
// Canvas rendering helpers
// ---------------------------------------------------------------------------

const resolveStyle = (feature, mapStyle, styleCondition) => {
  if (styleCondition && feature.properties) {
    const condValue = feature.properties[styleCondition];
    if (Object.prototype.hasOwnProperty.call(mapStyle, condValue)) {
      return mapStyle[condValue];
    }
  }
  return mapStyle || {};
};

const drawFeature = (ctx, rawGeometry, type, style) => {
  const scale = TILE_SIZE / EXTENT;

  ctx.beginPath();
  for (const ring of rawGeometry) {
    if (ring.length === 0) {
      continue; // eslint-disable-line no-continue
    }
    if (type === 1) {
      for (const pt of ring) {
        ctx.arc(pt.x * scale, pt.y * scale, 4, 0, 2 * Math.PI);
      }
    } else {
      ctx.moveTo(ring[0].x * scale, ring[0].y * scale);
      ring.slice(1).forEach(pt => {
        ctx.lineTo(pt.x * scale, pt.y * scale);
      });
      if (type === 3) {
        ctx.closePath();
      }
    }
  }

  const strokeColor = style.strokeColor;
  const strokeWeight = style.strokeWeight || 1;
  const strokeOpacity = style.strokeOpacity || 1;

  if (strokeColor) {
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWeight;
    ctx.globalAlpha = strokeOpacity;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  if (type === 3 && style.fillColor) {
    ctx.fillStyle = style.fillColor;
    ctx.globalAlpha = style.fillOpacity || 0.3;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VectorTileLayer = ({ name }) => {
  const dispatch = useDispatch();
  const map = useGoogleMap();
  const layer = useSelector(state => state.layers[name]);

  // Active features for the tiles currently shown on screen (used for click detection)
  const featuresRef = useRef({});
  // Persistent cache: tileKey → { canvas, features }
  // Kept across tile release/re-request cycles to avoid redundant fetches
  const tileCacheRef = useRef({});
  const mapStyleRef = useRef(layer && layer.map_style);
  const styleConditionRef = useRef(layer && layer.style_condition);

  useEffect(() => {
    mapStyleRef.current = layer && layer.map_style;
    styleConditionRef.current = layer && layer.style_condition;
  }, [layer]);

  useEffect(() => {
    if (!map || !layer || !layer.tile_base_url) {
      return void 0; // eslint-disable-line no-void
    }

    const pmTiles = new PMTiles(layer.tile_base_url);
    const layerName = name;

    const mapTypeObj = {
      tileSize: new google.maps.Size(TILE_SIZE, TILE_SIZE),
      maxZoom: 22,
      minZoom: 0,
      name,

      getTile(coord, zoom, ownerDocument) {
        const tileKey = `${zoom}/${coord.x}/${coord.y}`;

        // Cache hit: return the already-rendered canvas immediately
        const cached = tileCacheRef.current[tileKey];
        if (cached) {
          featuresRef.current[tileKey] = cached.features;
          return cached.canvas;
        }

        const canvas = ownerDocument.createElement('canvas');
        canvas.width = TILE_SIZE;
        canvas.height = TILE_SIZE;

        pmTiles.getZxy(zoom, coord.x, coord.y)
          .then(tile => {
            if (!tile || !tile.data || tile.data.byteLength === 0) {
              return;
            }
            const pbf = new Pbf(tile.data);
            const vectorTile = new VectorTile(pbf);
            const tileLayer = vectorTile.layers[layerName]
              || Object.values(vectorTile.layers)[0];

            if (!tileLayer) {
              return;
            }

            const ctx = canvas.getContext('2d');
            const features = [];

            for (let featureIdx = 0; featureIdx < tileLayer.length; featureIdx += 1) {
              const feature = tileLayer.feature(featureIdx);
              const rawGeom = feature.loadGeometry();
              const style = resolveStyle(
                feature, mapStyleRef.current, styleConditionRef.current
              );

              drawFeature(ctx, rawGeom, feature.type, style);

              features.push({
                id: feature.id || feature?.properties?._id,
                attrs: feature.properties,
                type: feature.type,
                rings: geomToWgs84(rawGeom, coord.x, coord.y, zoom)
              });
            }

            // Evict oldest entry when cache is full (simple FIFO)
            const cacheKeys = Object.keys(tileCacheRef.current);
            if (cacheKeys.length >= TILE_CACHE_MAX) {
              delete tileCacheRef.current[cacheKeys[0]];
            }

            tileCacheRef.current[tileKey] = { canvas, features };
            featuresRef.current[tileKey] = features;
          })
          .catch(() => {/* silently ignore errors */});

        return canvas;
      },

      releaseTile(canvas) {
        if (!canvas) {
          return;
        }
        // Remove from active features (click detection) but keep in tileCacheRef
        // so a future getTile for the same coordinates skips the network round-trip.
        const tileKey = Object.keys(tileCacheRef.current).find(
          key => tileCacheRef.current[key] && tileCacheRef.current[key].canvas === canvas
        );
        if (tileKey) {
          delete featuresRef.current[tileKey];
        }
      }
    };

    map.overlayMapTypes.push(mapTypeObj);

    const clickListener = map.addListener('click', event => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const zoom = map.getZoom();

      const tileCount = Math.pow(2, zoom);
      const degPerTile = 360 / tileCount;
      const threshold = (degPerTile / TILE_SIZE) * CLICK_THRESHOLD_PX;
      const thresholdSq = threshold * threshold;

      let closest = null;
      let closestDistSq = Infinity;

      // Search all loaded tiles — more robust than computing a single tile key,
      // which can miss features that cross tile boundaries or tiles evicted mid-session.
      Object.values(featuresRef.current).forEach(tileFeatures => {
        tileFeatures.forEach(feature => {
          const dist = distSqToFeature(feature.rings, feature.type, lat, lng);
          if (dist < thresholdSq && dist < closestDistSq) {
            closestDistSq = dist;
            closest = feature;
          }
        });
      });

      if (closest) {
        dispatch(openLayerTray({ id: closest.id, type: name }));
      }
    });

    return () => {
      const index = map.overlayMapTypes
        .getArray()
        .findIndex(mapType => mapType === mapTypeObj);
      if (index !== -1) {
        map.overlayMapTypes.removeAt(index);
      }
      google.maps.event.removeListener(clickListener);
      featuresRef.current = {};
      tileCacheRef.current = {};
    };
  }, [map, name]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

VectorTileLayer.propTypes = {
  name: PropTypes.string
};

export default VectorTileLayer;
